import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { faithService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const FaithContentPage = ({ currentUser }) => {
  const { day } = useParams();
  const navigate = useNavigate();
  
  const [content, setContent] = useState(null);
  const [journalEntry, setJournalEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [formData, setFormData] = useState({
    reflectionResponse: '',
    prayerNotes: '',
    spiritualMoodScale: 5
  });

  // Redirect if faith pillar not enabled
  useEffect(() => {
    if (currentUser && !currentUser.faithPreferences?.faithTrackEnabled) {
      navigate('/dashboard');
      return;
    }
  }, [currentUser, navigate]);

  // Load content for the day
  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      setMessage({ type: '', text: '' });

      try {
        const dayNum = day ? parseInt(day) : currentUser?.currentDay;
        
        if (!dayNum || dayNum < 1 || dayNum > 365) {
          setMessage({ type: 'error', text: 'Invalid day number' });
          return;
        }

        const contentResponse = await faithService.getContentForDay(dayNum);
        setContent(contentResponse.data.data);

        // Try to load existing journal entry
        try {
          const entryResponse = await faithService.getJournalEntry(currentUser._id, dayNum);
          const entry = entryResponse.data?.data;

          if (entry) {
            setJournalEntry(entry);
            setFormData({
              reflectionResponse: entry.reflectionResponse || '',
              prayerNotes: entry.prayerNotes || '',
              spiritualMoodScale: entry.spiritualMoodScale || 5
            });
          } else {
            setJournalEntry(null);
          }
        } catch (error) {
          // No entry yet is fine
          if (error.response?.status !== 404) {
            console.error('Error loading journal entry:', error);
          }
        }
      } catch (error) {
        console.error('Error loading faith content:', error);
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load faith content'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadContent();
    }
  }, [day, currentUser]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'spiritualMoodScale' ? parseInt(value) : value
    });
  };

  const handleSubmitJournalEntry = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const dayNum = day ? parseInt(day) : currentUser?.currentDay;
      
      if (!formData.reflectionResponse && !formData.prayerNotes) {
        setMessage({
          type: 'error',
          text: 'Please write at least one reflection or prayer note'
        });
        setIsSubmitting(false);
        return;
      }

      await faithService.createJournalEntry(currentUser._id, {
        day: dayNum,
        ...formData
      });

      setMessage({
        type: 'success',
        text: 'Your faith journal entry has been saved'
      });
      setShowJournalForm(false);

      // Reload entry
      const entryResponse = await faithService.getJournalEntry(currentUser._id, dayNum);
      setJournalEntry(entryResponse.data.data);
    } catch (error) {
      console.error('Error saving journal entry:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save journal entry'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser?.faithPreferences?.faithTrackEnabled) {
    return null;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const dayNum = day ? parseInt(day) : currentUser?.currentDay;
  const primaryTeaching = content
    ? (content.scriptureReference || content.scriptureText
      ? {
          reference: content.scriptureReference,
          text: content.scriptureText
        }
      : {
          reference: content.teachingReference,
          text: content.sourceTeaching
        })
    : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Navigation */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
        >
          ← Back to Dashboard
        </button>
        <span className="text-sm font-medium text-neutral-600">
          Day {dayNum} of {currentUser?.programLength || 365}
        </span>
      </div>

      {message.text && (
        <div
          className={`
            p-4 rounded-lg mb-6 text-sm
            ${message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
            }
          `}
          role="alert"
        >
          {message.text}
        </div>
      )}

      {content ? (
        <div className="space-y-8">
          {/* Primary Teaching Section */}
          <div className="bg-white rounded-lg shadow-sm p-8 border-l-4 border-amber-500">
            <h2 className="text-2xl font-serif font-semibold text-amber-900 mb-4">
              {content.title}
            </h2>
            
            {primaryTeaching && (primaryTeaching.reference || primaryTeaching.text) && (
              <div className="mb-6">
                {primaryTeaching.reference && (
                  <p className="text-sm font-semibold text-amber-600 mb-2">
                    {primaryTeaching.reference}
                  </p>
                )}
                {primaryTeaching.text && (
                  <blockquote className="italic text-neutral-700 leading-relaxed pl-4 border-l-2 border-amber-300">
                    "{primaryTeaching.text}"
                  </blockquote>
                )}
              </div>
            )}

            {/* Theme Tag */}
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                {content.themeTag}
              </span>
            </div>
          </div>

          {/* Devotional Content */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Today's Reflection
            </h3>
            <div className="prose prose-sm max-w-none text-neutral-700 whitespace-pre-wrap leading-relaxed">
              {content.devotionalContent}
            </div>
          </div>

          {/* Wellness Connection */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">
              Wellness Connection
            </h3>
            <p className="text-amber-800 leading-relaxed">
              {content.recoveryConnection}
            </p>
          </div>

          {/* Journal Entry Section */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                  Your Response
                </h3>
                <p className="text-sm text-neutral-600">
                  {journalEntry?.completed 
                    ? 'Entry completed' 
                    : 'Share your thoughts and reflections'}
                </p>
              </div>
              {journalEntry?.completed && (
                <span className="text-sm font-semibold text-amber-700 bg-amber-50 px-3 py-1 rounded">
                  ✓ Completed
                </span>
              )}
            </div>

            {!showJournalForm && !journalEntry?.completed ? (
              <button
                onClick={() => setShowJournalForm(true)}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Write Response
              </button>
            ) : showJournalForm || !journalEntry?.completed ? (
              <form onSubmit={handleSubmitJournalEntry} className="space-y-4">
                {/* Reflection Prompt */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {content.reflectionPrompt}
                  </label>
                  <textarea
                    name="reflectionResponse"
                    value={formData.reflectionResponse}
                    onChange={handleFormChange}
                    placeholder="Take a moment to reflect on how this scripture applies to your journey..."
                    rows={6}
                    className="
                      w-full px-4 py-3 border border-neutral-300 rounded-lg 
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                      text-neutral-900
                    "
                  />
                </div>

                {/* Prayer Prompt (optional) */}
                {content.prayerPrompt && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {content.prayerPrompt} <span className="text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      name="prayerNotes"
                      value={formData.prayerNotes}
                      onChange={handleFormChange}
                      placeholder="Express your thoughts, prayers, or concerns..."
                      rows={4}
                      className="
                        w-full px-4 py-3 border border-neutral-300 rounded-lg 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        text-neutral-900
                      "
                    />
                  </div>
                )}

                {/* Spiritual Mood Scale */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Spiritual Connection <span className="text-gray-500">(1-10)</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      name="spiritualMoodScale"
                      min="1"
                      max="10"
                      value={formData.spiritualMoodScale}
                      onChange={handleFormChange}
                      className="flex-1 h-2 bg-gradient-to-r from-red-300 to-green-500 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-lg font-semibold text-neutral-900 min-w-[3rem]">
                      {formData.spiritualMoodScale}/10
                    </span>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                      px-6 py-2 bg-amber-600 text-white rounded-lg 
                      hover:bg-amber-700 transition font-medium
                      disabled:bg-gray-400 disabled:cursor-not-allowed
                    "
                  >
                    {isSubmitting ? 'Saving...' : 'Save Response'}
                  </button>
                  {journalEntry?.completed && (
                    <button
                      type="button"
                      onClick={() => setShowJournalForm(false)}
                      className="
                        px-6 py-2 bg-neutral-200 text-neutral-800 rounded-lg 
                        hover:bg-neutral-300 transition font-medium
                      "
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowJournalForm(true)}
                className="text-amber-600 hover:text-amber-800 font-medium"
              >
                Edit Response
              </button>
            )}
          </div>

          {/* Estimated Time */}
          <div className="text-center text-sm text-neutral-600">
            Estimated time: {content.estimatedTime} minutes
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800 font-medium">
            Faith content not yet available for day {dayNum}
          </p>
          <p className="text-yellow-700 text-sm mt-2">
            Check back soon or select another day from your dashboard
          </p>
        </div>
      )}
    </div>
  );
};

export default FaithContentPage;
